import React from "react";

import {
    Plus,
    Trash2,
} from "lucide-react";

import {
    createEmptyHint,
    createEmptyProjectStep,
} from "../../../../utils/projectFactories";

export default function ProjectEditor({
                                          contentJson,
                                          setContentJson,
                                      }) {

    const steps = contentJson?.steps || [];

    const updateContent = (updater) => {
        const next = structuredClone(contentJson);

        updater(next);

        setContentJson(next);
    };

    const addStep = () => {
        updateContent((next) => {

            next.steps = next.steps || [];

            next.steps.push(
                createEmptyProjectStep()
            );
        });
    };

    const deleteStep = (stepIndex) => {
        updateContent((next) => {

            next.steps.splice(stepIndex, 1);

        });
    };

    const updateStep = (
        stepIndex,
        patch
    ) => {
        updateContent((next) => {

            next.steps[stepIndex] = {
                ...next.steps[stepIndex],
                ...patch,
            };

        });
    };

    const addHint = (stepIndex) => {
        updateContent((next) => {

            next.steps[stepIndex]
                .hints
                .push(createEmptyHint());

        });
    };

    return (
        <div className="space-y-6">

            {/* HEADER */}
            <div className="flex justify-between items-center">

                <h2 className="font-bold text-xl">
                    مراحل پروژه
                </h2>

                <button
                    onClick={addStep}
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white flex items-center gap-2"
                >
                    <Plus size={18}/>
                    افزودن مرحله
                </button>

            </div>

            {/* EMPTY */}
            {
                steps.length === 0 && (
                    <div className="bg-white border rounded-2xl p-5 text-gray-500">
                        هنوز مرحله‌ای وجود ندارد
                    </div>
                )
            }

            {/* STEPS */}
            {
                steps.map((step, stepIndex) => (
                    <div
                        key={step.id}
                        className="bg-white border rounded-2xl p-5 space-y-6"
                    >

                        {/* STEP HEADER */}
                        <div className="flex items-center gap-3">

                            <input
                                value={step.title}
                                onChange={(e) =>
                                    updateStep(stepIndex, {
                                        title: e.target.value,
                                    })
                                }
                                placeholder="عنوان مرحله"
                                className="flex-1 p-3 border rounded-xl bg-gray-50"
                            />

                            <button
                                onClick={() =>
                                    deleteStep(stepIndex)
                                }
                                className="p-2 rounded-xl hover:bg-red-50 text-red-600"
                            >
                                <Trash2 size={18}/>
                            </button>

                        </div>

                        {/* BEFORE */}
                        <div className="space-y-2">

                            <div className="font-medium">
                                توضیحات قبل
                            </div>

                            {
                                step.content.before.map((item, i) => (
                                    <textarea
                                        key={i}
                                        rows={3}
                                        value={item}
                                        onChange={(e) => {

                                            const next = [
                                                ...step.content.before,
                                            ];

                                            next[i] = e.target.value;

                                            updateStep(stepIndex, {
                                                content: {
                                                    ...step.content,
                                                    before: next,
                                                },
                                            });
                                        }}
                                        className="w-full p-3 border rounded-xl bg-gray-50"
                                    />
                                ))
                            }

                            <button
                                onClick={() =>
                                    updateStep(stepIndex, {
                                        content: {
                                            ...step.content,
                                            before: [
                                                ...step.content.before,
                                                "",
                                            ],
                                        },
                                    })
                                }
                                className="text-sm text-blue-600"
                            >
                                + افزودن توضیح
                            </button>

                        </div>

                        {/* INSTRUCTION */}
                        <div className="space-y-2">

                            <div className="font-medium">
                                دستورالعمل
                            </div>

                            {
                                step.content.instruction.map((item, i) => (
                                    <textarea
                                        key={i}
                                        rows={4}
                                        value={item}
                                        onChange={(e) => {

                                            const next = [
                                                ...step.content.instruction,
                                            ];

                                            next[i] = e.target.value;

                                            updateStep(stepIndex, {
                                                content: {
                                                    ...step.content,
                                                    instruction: next,
                                                },
                                            });
                                        }}
                                        className="w-full p-3 border rounded-xl bg-gray-50"
                                    />
                                ))
                            }

                            <button
                                onClick={() =>
                                    updateStep(stepIndex, {
                                        content: {
                                            ...step.content,
                                            instruction: [
                                                ...step.content.instruction,
                                                "",
                                            ],
                                        },
                                    })
                                }
                                className="text-sm text-blue-600"
                            >
                                + افزودن دستورالعمل
                            </button>

                        </div>

                        {/* AFTER */}
                        <div className="space-y-2">

                            <div className="font-medium">
                                توضیحات بعد
                            </div>

                            {
                                step.content.after.map((item, i) => (
                                    <textarea
                                        key={i}
                                        rows={3}
                                        value={item}
                                        onChange={(e) => {

                                            const next = [
                                                ...step.content.after,
                                            ];

                                            next[i] = e.target.value;

                                            updateStep(stepIndex, {
                                                content: {
                                                    ...step.content,
                                                    after: next,
                                                },
                                            });
                                        }}
                                        className="w-full p-3 border rounded-xl bg-gray-50"
                                    />
                                ))
                            }

                            <button
                                onClick={() =>
                                    updateStep(stepIndex, {
                                        content: {
                                            ...step.content,
                                            after: [
                                                ...step.content.after,
                                                "",
                                            ],
                                        },
                                    })
                                }
                                className="text-sm text-blue-600"
                            >
                                + افزودن توضیح
                            </button>

                        </div>

                        {/* STARTER CODE */}
                        <div className="space-y-2">

                            <div className="font-medium">
                                کد اولیه
                            </div>

                            <textarea
                                rows={8}
                                value={step.editor.starterCode}
                                onChange={(e) =>
                                    updateStep(stepIndex, {
                                        editor: {
                                            ...step.editor,
                                            starterCode: e.target.value,
                                        },
                                    })
                                }
                                className="w-full p-3 border rounded-xl bg-gray-50 font-mono"
                            />

                        </div>

                        {/* SOLUTION */}
                        <div className="space-y-2">

                            <div className="font-medium">
                                پاسخ
                            </div>

                            <textarea
                                rows={8}
                                value={step.solution.code}
                                onChange={(e) =>
                                    updateStep(stepIndex, {
                                        solution: {
                                            ...step.solution,
                                            code: e.target.value,
                                        },
                                    })
                                }
                                className="w-full p-3 border rounded-xl bg-gray-50 font-mono"
                            />

                        </div>

                        {/* VALIDATION */}
                        <div className="space-y-3">

                            <div className="font-medium">
                                اعتبارسنجی
                            </div>

                            <select
                                value={step.validation.type}
                                onChange={(e) =>
                                    updateStep(stepIndex, {
                                        validation: {
                                            ...step.validation,
                                            type: e.target.value,
                                        },
                                    })
                                }
                                className="p-3 border rounded-xl bg-white"
                            >
                                <option value="exact">
                                    exact
                                </option>

                                <option value="contains">
                                    contains
                                </option>

                                <option value="regex">
                                    regex
                                </option>

                                <option value="stdout">
                                    stdout
                                </option>
                            </select>

                            <textarea
                                rows={4}
                                value={step.validation.value}
                                onChange={(e) =>
                                    updateStep(stepIndex, {
                                        validation: {
                                            ...step.validation,
                                            value: e.target.value,
                                        },
                                    })
                                }
                                className="w-full p-3 border rounded-xl bg-white"
                            />

                        </div>

                        {/* FEEDBACK */}
                        <div className="space-y-3">

                            <div className="font-medium">
                                پیام موفقیت
                            </div>

                            {
                                step.feedback.correct.map((item, i) => (
                                    <textarea
                                        key={i}
                                        rows={2}
                                        value={item}
                                        onChange={(e) => {

                                            const next = [
                                                ...step.feedback.correct,
                                            ];

                                            next[i] = e.target.value;

                                            updateStep(stepIndex, {
                                                feedback: {
                                                    ...step.feedback,
                                                    correct: next,
                                                },
                                            });
                                        }}
                                        className="w-full p-3 border rounded-xl bg-gray-50"
                                    />
                                ))
                            }

                            <div className="font-medium">
                                پیام خطا
                            </div>

                            {
                                step.feedback.incorrect.map((item, i) => (
                                    <textarea
                                        key={i}
                                        rows={2}
                                        value={item}
                                        onChange={(e) => {

                                            const next = [
                                                ...step.feedback.incorrect,
                                            ];

                                            next[i] = e.target.value;

                                            updateStep(stepIndex, {
                                                feedback: {
                                                    ...step.feedback,
                                                    incorrect: next,
                                                },
                                            });
                                        }}
                                        className="w-full p-3 border rounded-xl bg-gray-50"
                                    />
                                ))
                            }

                        </div>

                        {/* HINTS */}
                        <div className="space-y-4">

                            <div className="font-medium">
                                هینت‌ها
                            </div>

                            {
                                step.hints.map((hint, hintIndex) => (
                                    <div
                                        key={hint.id}
                                        className="border rounded-xl p-4 bg-gray-50 space-y-3"
                                    >

                                        <textarea
                                            rows={2}
                                            placeholder="متن هینت"
                                            value={hint.text}
                                            onChange={(e) => {

                                                const nextHints = [
                                                    ...step.hints,
                                                ];

                                                nextHints[hintIndex] = {
                                                    ...nextHints[hintIndex],
                                                    text: e.target.value,
                                                };

                                                updateStep(stepIndex, {
                                                    hints: nextHints,
                                                });
                                            }}
                                            className="w-full p-3 border rounded-xl bg-white"
                                        />

                                        <textarea
                                            rows={5}
                                            placeholder="کد هینت"
                                            value={hint.code}
                                            onChange={(e) => {

                                                const nextHints = [
                                                    ...step.hints,
                                                ];

                                                nextHints[hintIndex] = {
                                                    ...nextHints[hintIndex],
                                                    code: e.target.value,
                                                };

                                                updateStep(stepIndex, {
                                                    hints: nextHints,
                                                });
                                            }}
                                            className="w-full p-3 border rounded-xl bg-white font-mono"
                                        />

                                    </div>
                                ))
                            }

                            <button
                                onClick={() =>
                                    addHint(stepIndex)
                                }
                                className="text-sm text-blue-600"
                            >
                                + افزودن هینت
                            </button>

                        </div>

                    </div>
                ))
            }

        </div>
    );
}